import React from 'react'
import { Badge } from './ui/badge'
import { MapPin, Phone, Mail, Globe, User, Calendar, Building2 } from 'lucide-react'
import { useDesaDetail } from '../hooks/use-desa-public'
import { StrukturOrganisasiPublicStruktur } from '@/types'
import { isKepalaDesa } from '@tatatemplate/shared-lib'

// Simple Loading Component
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
  </div>
)

// Simple Alert Component  
const Alert = ({ children, variant = "default" }: { children: React.ReactNode, variant?: "default" | "destructive" }) => (
  <div className={`p-4 rounded-lg border ${variant === "destructive" ? "bg-red-50 border-red-200 text-red-800" : "bg-blue-50 border-blue-200 text-blue-800"}`}>
    {children}
  </div>
)

interface DesaDetailCardProps {
  strukturPemerintahan?: StrukturOrganisasiPublicStruktur
}

export function DesaDetailCard({ strukturPemerintahan }: DesaDetailCardProps) {
  const { data: desa, isLoading, error } = useDesaDetail()

  // Extract kepala desa from struktur pemerintahan
  const kepalaDesaData = strukturPemerintahan?.detailStruktur?.find(detail =>
    isKepalaDesa(detail.namaJabatan) || isKepalaDesa(detail.jabatan?.nama)
  )

  if (isLoading) {
    return (
      <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <LoadingSkeleton />
        <div className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <LoadingSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        Gagal memuat detail desa. Silakan coba lagi nanti.
      </Alert>
    )
  }

  if (!desa) {
    return (
      <Alert>
        Data desa tidak ditemukan.
      </Alert>
    )
  }

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="p-8 space-y-8">
        {/* Header dengan Logo */}
        <div className="space-y-4">
          <div className="flex items-start gap-6">
            {desa.logoUrl && (
              <div className="shrink-0">
                <img
                  src={desa.logoUrl}
                  alt={`Logo ${desa.name}`}
                  className="w-24 h-24 object-contain rounded-xl border-2 border-gray-200 bg-white p-3 shadow-sm"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-3xl font-bold text-gray-900 mb-3">{desa.name}</h3>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge variant="default" className="bg-blue-100 text-blue-800 font-medium hover:bg-blue-100 px-3 py-1.5">
                  {desa.code}
                </Badge>
                {desa.postalCode && (
                  <Badge variant="outline" className="border-gray-300 px-3 py-1.5">
                    Kode Pos: {desa.postalCode}
                  </Badge>
                )}
              </div>
              {desa.description && (
                <p className="text-gray-600 leading-relaxed">{desa.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Informasi Kepala Desa */}
        {kepalaDesaData && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <User className="w-5 h-5" />
              {kepalaDesaData.jabatan?.nama || kepalaDesaData.namaJabatan || 'Kepala Desa'}
            </h3>
            {kepalaDesaData.namaPejabat && (
              <p className="font-medium text-lg text-gray-900 mb-2">{kepalaDesaData.namaPejabat}</p>
            )}
            {kepalaDesaData.jabatan?.deskripsi && (
              <p className="text-sm text-gray-600">{kepalaDesaData.jabatan.deskripsi}</p>
            )}
          </div>
        )}

        {/* Informasi Kontak - Grid Layout */}
        <div className="space-y-6">
          <h3 className="font-bold text-gray-900 text-xl flex items-center gap-2">
            <div className="w-2 h-6 bg-blue-600 rounded-full"></div>
            Informasi Kontak
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {desa.address && (
              <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <MapPin className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Alamat</p>
                  <p className="text-gray-600 leading-relaxed">{desa.address}</p>
                </div>
              </div>
            )}

            {desa.phone && (
              <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Phone className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Telepon</p>
                  <p className="text-gray-600">{desa.phone}</p>
                </div>
              </div>
            )}

            {desa.mobile && (
              <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Phone className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Mobile</p>
                  <p className="text-gray-600">{desa.mobile}</p>
                </div>
              </div>
            )}

            {desa.email && (
              <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Mail className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Email</p>
                  <a
                    href={`mailto:${desa.email}`}
                    className="text-blue-600 hover:text-blue-700 hover:underline font-medium transition-colors"
                  >
                    {desa.email}
                  </a>
                </div>
              </div>
            )}

            {desa.website && (
              <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Globe className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Website</p>
                  <a
                    href={desa.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline font-medium transition-colors"
                  >
                    {desa.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}